import * as React from 'react';
import { InjectedIntlProps, injectIntl } from 'react-intl';
import { connect } from 'react-redux';

import scrollToTop from '../../../hoc/scrollToTop';

import { UserContextConsumer } from '../../../../contexts/UserContext';
import * as IBooks from '../../../../interfaces/merchants/resources/books';
import * as IInputs from '../../../../interfaces/merchants/resources/books/inputs';

import {
  addToWaitingList,
  setDateTimePicker,
} from '../../../../redux/actions/merchants/resources/books';
import { DateTimePickerQuery } from '../../../../redux/actions/merchants/resources/books/api';
import {
  inputBookingEventTimeSlot,
  RestartReservationError,
} from '../../../../redux/actions/merchants/resources/books/inputs';
import { Dispatch } from '../../../../redux/reducers/merchants/resources/books/inputs';

import { default as Calendar } from './InputBookingEventDateTimeCalendar';
import { default as List } from './InputBookingEventDateTimeList';

interface ElementProps {
  modelType: IBooks.ModelType;
  settings: IBooks.SettingEvent;
  postable: boolean;
  onSubmit: () => void;
  onRestart: () => void;
  renew?: boolean;
  withExpressFlow: boolean;
}

interface StateProps {
  inputs: IInputs.State;
}

interface DispatchProps {
  actions: {
    setDateTimePicker: (q: DateTimePickerQuery) => Promise<void>;
    addToWaitingList: (
      resourceId: string,
      timeSlot: IBooks.EventTimeSlot,
      lastName?: string,
      firstName?: string,
      email?: string,
    ) => Promise<void>;
    handleSubmit: (
      modelType: IBooks.ModelType,
      merchantId: string,
      resourceId: string,
      timeSlot: IInputs.EventTimeSlot,
      withExpressFlow: boolean,
      reservationId?: number,
    ) => Promise<void>;
  };
}

type Props = ElementProps & StateProps & DispatchProps & InjectedIntlProps;

interface State {
  eventTimeSlot: IInputs.EventTimeSlot | undefined;
}

interface SetDateTimeQuery {
  start: Date;
  end: Date;
}

class InputBookingEventDateTime extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    const {
      renew,
      inputs: { eventTimeSlot },
    } = this.props;
    this.state = {
      eventTimeSlot: renew ? undefined : eventTimeSlot,
    };
    this.handleSetDateTime = this.handleSetDateTime.bind(this);
    this.handleAddToWaitingList = this.handleAddToWaitingList.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
  }

  private async handleSetDateTime(query?: SetDateTimeQuery) {
    const {
      actions,
      inputs: { merchantId, resourceId },
    } = this.props;
    const start = !!query ? query.start : null;
    const end = !!query ? query.end : null;
    await actions.setDateTimePicker({
      merchantId: merchantId!,
      resourceId: resourceId!,
      start,
      end,
    });
  }

  private async handleAddToWaitingList(
    timeSlot: IBooks.EventTimeSlot | undefined,
    lastName?: string,
    firstName?: string,
    email?: string,
    onReturn?: () => void,
  ) {
    const {
      actions,
      inputs: { resourceId },
    } = this.props;
    try {
      await actions.addToWaitingList(
        resourceId!,
        timeSlot!,
        lastName,
        firstName,
        email,
      );
      if (onReturn) {
        onReturn();
      }
    } catch (e) {
      // failed addToWaitingList.
    }
  }

  private async handleSubmit(timeSlot: IInputs.EventTimeSlot) {
    const {
      modelType,
      inputs,
      onSubmit,
      onRestart,
      withExpressFlow,
    } = this.props;
    try {
      await this.props.actions.handleSubmit(
        modelType,
        inputs.merchantId!,
        inputs.resourceId!,
        timeSlot,
        withExpressFlow,
        inputs.reservationId,
      );
      onSubmit();
    } catch (e) {
      if (e instanceof RestartReservationError) {
        onRestart();
      }
    }
  }

  public render() {
    const { settings, postable, withExpressFlow } = this.props;
    const { eventTimeSlot } = this.state;
    return (
      <UserContextConsumer>
        {context =>
          settings.selectSlotsType === 'list' ? (
            <List
              loggedIn={context.auth.isLoggedIn}
              settings={settings}
              postable={postable}
              onSetDateTimePicker={this.handleSetDateTime}
              onAddToWaitingList={this.handleAddToWaitingList}
              onSubmit={this.handleSubmit}
              withExpressFlow={withExpressFlow}
            />
          ) : (
            <Calendar
              loggedIn={context.auth.isLoggedIn}
              initialEventTimeSlot={eventTimeSlot}
              settings={settings}
              postable={postable}
              onSetDateTimePicker={this.handleSetDateTime}
              onAddToWaitingList={this.handleAddToWaitingList}
              onSubmit={this.handleSubmit}
              withExpressFlow={withExpressFlow}
            />
          )
        }
      </UserContextConsumer>
    );
  }
}

const mapStateProps = ({
  merchantsResourcesBooksInputs,
}: {
  merchantsResourcesBooksInputs: IInputs.State;
}): StateProps => ({
  inputs: merchantsResourcesBooksInputs,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  actions: {
    setDateTimePicker: (q: DateTimePickerQuery) =>
      setDateTimePicker(q)(dispatch),
    addToWaitingList: (
      resourceId: string,
      timeSlot: IBooks.EventTimeSlot,
      lastName?: string,
      firstName?: string,
      email?: string,
    ) =>
      addToWaitingList(
        resourceId,
        timeSlot,
        lastName,
        firstName,
        email,
      )(dispatch),
    handleSubmit: (
      modelType: IBooks.ModelType,
      merchantId: string,
      resourceId: string,
      timeSlot: IInputs.EventTimeSlot,
      withExpressFlow: boolean,
      reservationId?: number,
    ) =>
      inputBookingEventTimeSlot(
        modelType,
        merchantId,
        resourceId,
        timeSlot,
        withExpressFlow,
        reservationId,
      )(dispatch),
  },
});

export default connect(
  mapStateProps,
  mapDispatchToProps,
)(injectIntl(scrollToTop(InputBookingEventDateTime)));
